import React from 'react';
import { useDispatch } from 'react-redux';
import { StyledBlueToggle } from '../../components/common';
import SellersShieldTutorialGif from '../../gifs/sellers-shield-tutorial.gif';
import { IntegrationKey, EnablementStatus } from '../../hooks/integrations/types';
import { useToggleIntegration } from '../../hooks/integrations/use-toggle-integration';

import { ReactComponent as SellersShieldLogo } from '../../images/sellers-shield-logo.svg';
import { successToast } from '../../store/pageFrame/actions';
import { EnvelopeSentCard } from './EnvelopeSentCard';

type EnvelopeSentForSellersShieldProps = {
  onDone: () => void;
  onSignNow: () => void;
  showSignNowBtn: boolean;
  signNowButton?: React.ReactNode;
};

export function EnvelopeSentForSellersShield({
  onSignNow,
  onDone,
  showSignNowBtn,
  signNowButton,
}: EnvelopeSentForSellersShieldProps) {
  const dispatch = useDispatch();
  const toggleIntegration = useToggleIntegration();
  const [tryItNextTime, setTryItNextTime] = React.useState(false);

  async function handleEnableSellersShield(e: React.ChangeEvent<HTMLInputElement>) {
    const isChecked = e.target.checked;

    setTryItNextTime(isChecked);

    if (isChecked) {
      try {
        await toggleIntegration.mutateAsync(
          {
            integrationType: IntegrationKey.SellersShield,
            switchToEnablement: EnablementStatus.Active,
          },
          {
            onSuccess: () => {
              dispatch(successToast('Success! Sellers Shield is Enabled!'));
            },
          }
        );
      } catch (error) {
        setTryItNextTime(!isChecked);
        console.error('Failed to toggle Sellers Shield integration:', error);
      }
    }
  }

  return (
    <EnvelopeSentCard
      onDone={onDone}
      onSignNow={onSignNow}
      showSignNowBtn={showSignNowBtn}
      signNowButton={signNowButton}
      showAutomatedReminders
    >
      <article className="flex justify-evenly items-center mt-16 w-full rounded-xl bg-grey-50 max-sm:flex-col">
        <img
          className="border border-solid border-[#CAD5E0]"
          src={SellersShieldTutorialGif}
          alt="Sellers Shield"
          width={400}
        />

        <div className="flex flex-col gap-5 p-12 max-w-md">
          <SellersShieldLogo />

          <div>
            <h2 className="mb-0">Make your next deal easier. Lower legal risk and save time with Sellers Shield.</h2>

            <p className="text-lg leading-normal text-grey-800">
              Next time, try our <strong className="text-black">FREE</strong>, integrated disclosures platform, Seller
              Shield. Like TurboTax for disclosures, it saves time, reduces legal risk for you and your client, and
              delivers flawless, audit-ready forms straight to your SkySlope file.
            </p>
          </div>

          <div className="flex gap-3 items-center">
            <StyledBlueToggle checked={tryItNextTime} onChange={handleEnableSellersShield} disabled={tryItNextTime} />

            <p className="text-lg leading-normal text-grey-800">Try it Next Time!</p>
          </div>
        </div>
      </article>
    </EnvelopeSentCard>
  );
}
